import {
  Alert,
  Button,
  Group,
  Menu,
  Modal,
  Progress,
  Skeleton,
  Stack,
  Text,
  UnstyledButton,
} from '@mantine/core';
import {
  IconAlertTriangle,
  IconBook,
  IconCloudDownload,
  IconDatabase,
  IconDatabaseExclamation,
  IconDownload,
  IconFileAlert,
  IconFileCheck,
  IconFolderOpen,
  IconInfoCircle,
  IconRosetteDiscountCheck,
  IconSignature,
} from '@tabler/icons-react';
import { type FormEvent, type ReactNode, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ReauthField, useReauth } from '../../approval-policy.js';
import { formatDateTime } from '../../format.js';
import { Panel } from '../../Panel.js';
import { StatusBadge } from '../../StatusBadge.js';
import { instantDay } from './DocumentDetailsPanel.js';
import classes from './Documents.module.css';
import {
  documentHeading,
  formatFileSize,
  formatStorageSize,
  revisionText,
  sectionHref,
} from './library.js';
import type {
  DocumentBell,
  DocumentBellItem,
  DownloadQueueItem,
  DownloadsView,
  FolderImportState,
  LibraryDetail,
  LibraryRevision,
  ShipUploadsView,
} from './types.js';

/** Kuyruktaki satır: dosyanın indirme durumu (ADR 0032 #85, #86). */
export function queueItemFor(downloads: DownloadsView | null, fileId: string) {
  return downloads?.items.find((item) => item.fileId === fileId) ?? null;
}

/** Kuyruğun bekleme nedenleri: kendi metinleri var (`documents.notOnBoard.reasons`). */
const WAIT_REASONS = new Set(['paused', 'window', 'manual', 'monthly_cap']);

/**
 * Kartta gösterilen durum anahtarı (`documents.notOnBoard.reasons.<anahtar>`; inceleme F7):
 * "Downloading" yalnız kuyruğun o an indirdiği dosyada (`active`); yarım kalmış bayt sayısı
 * inmekte olduğunu göstermez (duraklatma, pencere dışı). Her bekleme nedeni kendi metniyle;
 * bağlantı hatasından sonra yeniden deneme bekleyen dosya "retry".
 */
export function queueStatusKey(
  item: DownloadQueueItem | null,
  active: DownloadsView['active'],
): string {
  if (!item) return 'unknown';
  if (item.state === 'failed' || item.state === 'deferred_quota') return item.state;
  if (item.state !== 'pending') return 'unknown';
  if (active?.fileId === item.fileId) return 'downloading';
  if (item.reason && WAIT_REASONS.has(item.reason)) return item.reason;
  if (item.reason) return 'retry';
  return 'pending';
}

/**
 * Zil maddesinin açtığı ekran (inceleme WF-8, F5): disk, kota ve indirme sorunları Settings'teki
 * depo kartında; revizyon ve doküman maddeleri kendi bölümünde ilk dokümanı açar. Hedef
 * bilinmiyorsa ISM Forms.
 */
export function bellPath(item: DocumentBellItem, targets: DocumentBell['targets']): string {
  if (item.kind === 'disk' || item.kind === 'quota' || item.kind === 'download_problems') {
    return '/settings';
  }
  const ids = 'revisionIds' in item ? item.revisionIds : item.documentIds;
  const target = ids.map((id) => targets?.[id]).find((value) => value !== undefined);
  return target ? sectionHref(target.section, { doc: target.documentId }) : sectionHref('ism');
}

/**
 * "Henüz gemide değil" (plan M8a Task 16): dosya gemi sunucusunda doğrulanmadan önizleyici yerine
 * bu kart çıkar: kuyruk sırası, boyut, bekleme nedeni ve sunucu yöneticisine "Download now"
 * (ADR 0028 #40 madde 5). Kaptan yetkisi yoksa yalnız durum görünür.
 */
export function NotOnBoardCard({
  item,
  active = null,
  size,
  canRequest,
  requested,
  onRequest,
}: {
  item: DownloadQueueItem | null;
  /** Kuyruğun o an indirdiği dosya. */
  active?: DownloadsView['active'] | undefined;
  size: number;
  canRequest: boolean;
  requested: boolean;
  onRequest: () => void;
}) {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage ?? 'en';
  const k = (key: string) => t(`documents.notOnBoard.${key}`);
  // İstenen indirme başarısız ya da ertelendiyse düğme yeniden çıkar (inceleme F7)
  const stillRequested = requested && item?.state !== 'failed' && item?.state !== 'deferred_quota';
  return (
    <div className={classes.notOnBoard} role="status">
      <span className={classes.notOnBoardIcon} aria-hidden>
        <IconCloudDownload size={28} stroke={1.6} />
      </span>
      <p className={classes.notOnBoardTitle}>{k('title')}</p>
      <p className={classes.notOnBoardText}>{k('description')}</p>
      <dl className={classes.notOnBoardFacts}>
        <div>
          <dt>{k('position')}</dt>
          <dd>{item?.position ?? '—'}</dd>
        </div>
        <div>
          <dt>{k('size')}</dt>
          <dd>{formatFileSize(size, locale)}</dd>
        </div>
        <div>
          <dt>{k('reason')}</dt>
          <dd>{t(`documents.notOnBoard.reasons.${queueStatusKey(item, active)}`)}</dd>
        </div>
      </dl>
      {stillRequested ? (
        <Text size="sm" c="dimmed">
          {k('requested')}
        </Text>
      ) : null}
      {canRequest && !stillRequested ? (
        <Button leftSection={<IconDownload size={16} />} onClick={onRequest}>
          {k('downloadNow')}
        </Button>
      ) : null}
    </div>
  );
}

/**
 * "Bu revizyonda ne değişti" şeridi (plan M8a Task 16; ADR 0028 #39 madde 3, #26, #30 D):
 * değişiklik özeti; teyit bekliyorsa kaptana Acknowledge (parola penceresi), verilmişse kim ve
 * ne zaman; geri çekilmede basılı kopyaları kaldırma uyarısı; ileri tarihli revizyonda ön bilgi.
 */
export function ChangesStrip({
  detail,
  revision,
  canAcknowledge,
  onAcknowledge,
  offsetMin,
}: {
  detail: LibraryDetail;
  revision: LibraryRevision;
  canAcknowledge: boolean;
  onAcknowledge: (kind: 'adopted' | 'withdrawn') => void;
  offsetMin?: number | undefined;
}) {
  const { t } = useTranslation();
  const due = detail.acknowledgementDue ?? null;
  const done = revision.acknowledgements?.find((a) =>
    due?.kind === 'withdrawn' ? a.kind === 'withdrawn' : a.kind === 'adopted',
  );
  const withdrawn = detail.withdrawn ?? null;
  let message: ReactNode = revision.changeSummary;
  let title = t('documents.changes.title', { revision: revisionText(revision.revisionNo) });
  let tone: 'info' | 'danger' = 'info';
  if (withdrawn && !detail.current) {
    tone = 'danger';
    title = t('documents.changes.withdrawn', { revision: revisionText(withdrawn.revisionNo) });
    message = withdrawn.reason ?? null;
  } else if (detail.scheduled) {
    message = t('documents.changes.upcoming', {
      revision: revisionText(detail.scheduled.revisionNo),
      date: detail.scheduled.effectiveOn.split('-').reverse().join('.'),
    });
  }
  return (
    <section className={classes.changes} data-tone={tone} aria-label={title}>
      <span className={classes.changesIcon} aria-hidden>
        {tone === 'danger' ? <IconFileAlert size={20} /> : <IconInfoCircle size={20} />}
      </span>
      <div className={classes.changesText}>
        <p className={classes.changesTitle}>{title}</p>
        {message ? <p className={classes.changesBody}>{message}</p> : null}
        {done ? (
          <p className={classes.changesDone}>
            <IconRosetteDiscountCheck size={14} aria-hidden />
            {t('documents.changes.acknowledged', {
              name: done.byName ?? '—',
              date: formatDateTime(done.at, offsetMin),
            })}
          </p>
        ) : null}
      </div>
      {due && canAcknowledge && !done ? (
        <Button
          size="sm"
          leftSection={<IconSignature size={16} />}
          onClick={() => onAcknowledge(due.kind)}
        >
          {due.kind === 'withdrawn'
            ? t('documents.changes.acknowledgeWithdrawal')
            : t('documents.changes.acknowledge')}
        </Button>
      ) : null}
    </section>
  );
}

/**
 * Kaptanın teyidi (sorular #23; ADR 0028 #26): parola yeniden istenir ve kapatılamaz (mimari
 * belge §7.1). Hata kodu pencerede gösterilir; parola sunucuya ana süreç üzerinden gider.
 */
export function AcknowledgeDialog({
  opened,
  kind,
  detail,
  revision,
  onClose,
  onConfirm,
}: {
  opened: boolean;
  kind: 'adopted' | 'withdrawn';
  detail: LibraryDetail;
  revision: LibraryRevision;
  onClose: () => void;
  onConfirm: (password: string) => Promise<void>;
}) {
  const { t } = useTranslation();
  const k = (key: string, options?: Record<string, unknown>) =>
    t(`documents.acknowledge.${key}`, options ?? {});
  // Teyit ve "okudum"da parola isteğe bağlı (ADR 0056 §1): alan yerine bilgi metni; şirket ayarı
  // açıksa ya da sunucu isterse alan görünür. Boş parola "parolasız" demektir
  const reauth = useReauth('document_acknowledgement');
  const { reset: resetReauth } = reauth;
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Pencere bağlı kalır (odak dönüşü; S5): kapanınca parola ve hata silinir
  useEffect(() => {
    if (opened) return;
    resetReauth();
    setError(null);
  }, [opened, resetReauth]);
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (reauth.missing) return;
    setBusy(true);
    setError(null);
    try {
      await onConfirm(reauth.body().password ?? '');
      reauth.reset();
    } catch (failure) {
      reauth.noteError(failure);
      const code = (failure as { code?: string }).code ?? 'GENERIC';
      setError(t(`documents.errors.${code}`, { defaultValue: t('documents.errors.GENERIC') }));
    } finally {
      setBusy(false);
    }
  };
  const values = { document: documentHeading(detail), revision: revisionText(revision.revisionNo) };
  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={kind === 'withdrawn' ? k('titleWithdrawal') : k('title')}
      closeButtonProps={{ 'aria-label': t('documents.screen.dismiss') }}
      centered
    >
      <form onSubmit={(event) => void submit(event)}>
        <Stack gap="md">
          <Text size="sm">
            {kind === 'withdrawn' ? k('bodyWithdrawal', values) : k('body', values)}
          </Text>
          {error ? (
            <Alert color="red" icon={<IconAlertTriangle size={16} />} role="alert">
              {error}
            </Alert>
          ) : null}
          <ReauthField
            reauth={reauth}
            label={k('password')}
            description={k('passwordHint')}
            mb="0"
          />
          <Group justify="flex-end">
            <Button variant="default" onClick={onClose}>
              {k('cancel')}
            </Button>
            <Button type="submit" loading={busy} disabled={reauth.missing}>
              {k('confirm')}
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}

const BELL_ICONS: Record<DocumentBell['items'][number]['kind'], ReactNode> = {
  acknowledge: <IconSignature size={16} />,
  acknowledge_withdrawal: <IconFileAlert size={16} />,
  download_problems: <IconAlertTriangle size={16} />,
  downloading: <IconCloudDownload size={16} />,
  upcoming: <IconInfoCircle size={16} />,
  withdrawn_no_current: <IconFileAlert size={16} />,
  read: <IconFileCheck size={16} />,
  disk: <IconDatabaseExclamation size={16} />,
  quota: <IconDatabaseExclamation size={16} />,
};

/**
 * Zilin doküman maddeleri (ADR 0028 #39): kaptana teyit ve indirme sorunları, herkese kendi
 * okumaları; "downloading", "upcoming" ve "withdrawn" listede görünür ama zile sayılmaz. Panodaki
 * uyarılarda da aynı liste.
 */
export function DocumentBellList({
  bell,
  onOpen,
}: {
  bell: DocumentBell | null;
  onOpen: (item: DocumentBell['items'][number]) => void;
}) {
  const { t } = useTranslation();
  if (!bell || bell.items.length === 0) return null;
  return (
    <ul className={classes.bellList} aria-label={t('documents.bell.title')}>
      {bell.items.map((item) => {
        let text: string;
        if (item.kind === 'disk') text = t(`documents.bell.disk.${item.level}`);
        else if (item.kind === 'quota') text = t('documents.bell.quota', { percent: item.percent });
        else text = t(`documents.bell.${item.kind}`, { count: item.count });
        return (
          <li key={item.kind}>
            <UnstyledButton
              className={classes.bellItem}
              data-counted={item.counted || undefined}
              onClick={() => onOpen(item)}
            >
              <span className={classes.bellIcon} aria-hidden>
                {BELL_ICONS[item.kind]}
              </span>
              <span>{text}</span>
            </UnstyledButton>
          </li>
        );
      })}
    </ul>
  );
}

/** İçe aktarmanın durum satırı (ilerleme ya da sonuç). */
function importText(
  state: FolderImportState,
  k: (key: string, options?: Record<string, unknown>) => string,
): string {
  if (state.kind === 'error') return k('importFailed');
  if (state.kind === 'done') {
    const { imported, failed } = state.summary;
    if (imported.length === 0 && failed.length === 0) return k('importNothing');
    return k('importDone', { count: imported.length, failed: failed.length });
  }
  const progress = state.progress;
  if (!progress) return k('progress.starting');
  return k(`progress.${progress.phase}`, progress);
}

const DISK_TONE: Record<string, 'success' | 'warning' | 'danger'> = {
  ok: 'success',
  low: 'warning',
  critical: 'danger',
};

/**
 * Settings'te gemideki doküman deposu (plan M8a Task 16; ADR 0032): kota ve disk kullanımı,
 * bekleyen ve başarısız dosyalar, toplu indirmenin durumu. İndirmeyi yöneten (`devices.create`)
 * toplu indirmeyi en çok 7 gün duraklatır ya da sürdürür (#40 madde 6).
 */
/** Kart yalnız dosya deposu olmayan gemi sunucusunda çizilmez; diğer hatada hata ve Retry (F6). */
export function storageCardHidden(code: string | null | undefined): boolean {
  return code === 'FILES_UNAVAILABLE';
}

export function DocumentStorageCard({
  view,
  canManage,
  error = null,
  onRetry,
  onPause,
  onResume,
  onImport,
  importState = null,
  uploads = null,
}: {
  /** `null`: okunuyor (ya da okunamadı; `error`). */
  view: DownloadsView | null;
  /**
   * Gemiden ofise yükleme kuyruğu (ADR 0038 §14; `GET /api/ship/v1/uploads`): bekleyen dosya ve
   * bayt, başarısız, durum. Yoksa bölüm çizilmez.
   */
  uploads?: ShipUploadsView | null | undefined;
  canManage: boolean;
  /** Okuma ya da işlem (duraklat, sürdür) hata kodu; kart kaybolmaz (inceleme F6). */
  error?: string | null | undefined;
  onRetry?: (() => void) | undefined;
  onPause: (days: number) => void;
  onResume: () => void;
  /** "Import from folder" (#33): klasörü gemi uygulaması seçer ve hash'ler. */
  onImport?: (() => void) | undefined;
  importState?: FolderImportState | null | undefined;
}) {
  const { t, i18n } = useTranslation();
  const k = (key: string, options?: Record<string, unknown>) =>
    t(`documents.storage.${key}`, options ?? {});
  const size = (bytes: number) => formatStorageSize(bytes, i18n.language);
  const u = (key: string) => t(`documents.library.shipUploads.${key}`);
  const alert = error ? (
    <Alert color="red" icon={<IconAlertTriangle size={16} />} role="alert">
      {t(`documents.errors.${error}`, { defaultValue: t('documents.errors.GENERIC') })}{' '}
      {onRetry ? (
        <Button size="compact-xs" variant="subtle" onClick={onRetry}>
          {t('documents.screen.retry')}
        </Button>
      ) : null}
    </Alert>
  ) : null;
  if (!view && error) {
    return (
      <Panel title={k('title')} icon={<IconDatabase size={18} />}>
        <div className={classes.storage}>{alert}</div>
      </Panel>
    );
  }
  if (!view) {
    return (
      <Panel title={k('title')} icon={<IconDatabase size={18} />}>
        <div aria-busy="true" className={classes.storage}>
          <Skeleton height={14} width="70%" />
          <Skeleton height={8} />
          <Skeleton height={14} width="50%" />
        </div>
      </Panel>
    );
  }
  const percent =
    view.quota.quotaBytes > 0
      ? Math.min(100, (view.quota.usedBytes / view.quota.quotaBytes) * 100)
      : 0;
  const waiting = view.items.filter(
    (item) => item.state === 'pending' || item.state === 'deferred_quota',
  ).length;
  const failed = view.items.filter((item) => item.state === 'failed').length;
  const level = DISK_TONE[view.disk.level] ? view.disk.level : 'ok';
  return (
    <Panel title={k('title')} icon={<IconDatabase size={18} />}>
      <div className={classes.storage}>
        {alert}
        <div className={classes.storageRow}>
          <span className={classes.storageLabel}>{k('quota')}</span>
          <span className={classes.storageValue}>
            {k('used', { used: size(view.quota.usedBytes), quota: size(view.quota.quotaBytes) })}
          </span>
        </div>
        <Progress
          value={percent}
          size="sm"
          // Renk token'dan (README "Tasarım değerleri"; inceleme M8a3-R29)
          color={percent >= 95 ? 'red' : percent >= 80 ? 'var(--felix-warning-500)' : 'brand'}
          aria-label={k('quota')}
        />
        <div className={classes.storageRow}>
          <span className={classes.storageLabel}>{k('disk')}</span>
          <span className={classes.storageValue}>
            {k('free', { free: size(view.disk.freeBytes) })}{' '}
            <StatusBadge tone={DISK_TONE[level] ?? 'success'}>{k(`levels.${level}`)}</StatusBadge>
          </span>
        </div>
        <div className={classes.storageRow}>
          <span className={classes.storageLabel}>{k('waiting')}</span>
          <span className={classes.storageValue}>{waiting}</span>
        </div>
        <div className={classes.storageRow}>
          <span className={classes.storageLabel}>{k('failed')}</span>
          <span className={classes.storageValue} data-alert={failed > 0 || undefined}>
            {failed}
          </span>
        </div>
        <div className={classes.storageRow}>
          <span className={classes.storageLabel}>{k('bulk')}</span>
          <span className={classes.storageValue}>
            {view.paused
              ? k('paused', { date: instantDay(view.paused.until), by: view.paused.byName ?? '—' })
              : k('running')}
          </span>
        </div>
        <Text size="xs" c="dimmed">
          {k('hint')}
        </Text>
        {uploads ? (
          <section className={classes.storageSection} aria-label={u('title')}>
            <p className={classes.storageSectionTitle}>{u('title')}</p>
            <div className={classes.storageRow}>
              <span className={classes.storageLabel}>{u('pendingFiles')}</span>
              <span className={classes.storageValue}>{uploads.pendingFiles}</span>
            </div>
            <div className={classes.storageRow}>
              <span className={classes.storageLabel}>{u('pendingBytes')}</span>
              <span className={classes.storageValue}>{size(uploads.pendingBytes)}</span>
            </div>
            <div className={classes.storageRow}>
              <span className={classes.storageLabel}>{u('failed')}</span>
              <span
                className={classes.storageValue}
                data-alert={uploads.failedFiles > 0 || undefined}
              >
                {uploads.failedFiles}
              </span>
            </div>
            <div className={classes.storageRow}>
              <span className={classes.storageLabel}>{u('state')}</span>
              <span className={classes.storageValue}>
                {t(`documents.library.summary.states.${uploads.state}`, {
                  defaultValue: uploads.state,
                })}
              </span>
            </div>
          </section>
        ) : null}
        {canManage && onImport ? (
          <div className={classes.storageImport}>
            <Button
              variant="light"
              size="xs"
              leftSection={<IconFolderOpen size={15} />}
              loading={importState?.kind === 'running'}
              onClick={onImport}
            >
              {k('import')}
            </Button>
            <Text size="xs" c="dimmed">
              {k('importHint')}
            </Text>
            {importState ? (
              <Text size="xs" role="status" className={classes.storageImportStatus}>
                {importText(importState, k)}
              </Text>
            ) : null}
          </div>
        ) : null}
        {canManage ? (
          view.paused ? (
            <Button variant="default" size="xs" onClick={onResume}>
              {k('resume')}
            </Button>
          ) : (
            <Menu position="bottom-start" withinPortal>
              <Menu.Target>
                <Button variant="default" size="xs">
                  {k('pause')}
                </Button>
              </Menu.Target>
              <Menu.Dropdown>
                {[1, 3, 7].map((days) => (
                  <Menu.Item key={days} onClick={() => onPause(days)}>
                    {k('pauseFor', { count: days })}
                  </Menu.Item>
                ))}
              </Menu.Dropdown>
            </Menu>
          )
        ) : null}
      </div>
    </Panel>
  );
}

/**
 * Kişisel okuma beyanı şeridi (ADR 0028 #29): okuma gereken yürürlükteki revizyonu kişi henüz
 * beyan etmediyse "Read and understood" ve parolalı "Confirm read".
 */
export function ReadStrip({
  revision,
  onConfirm,
}: {
  revision: LibraryRevision;
  onConfirm: () => void;
}) {
  const { t } = useTranslation();
  const k = (key: string, options?: Record<string, unknown>) =>
    t(`documents.read.${key}`, options ?? {});
  return (
    <section className={classes.changes} data-tone="info" aria-label={k('title')}>
      <span className={classes.changesIcon} aria-hidden>
        <IconBook size={20} />
      </span>
      <div className={classes.changesText}>
        <p className={classes.changesTitle}>{k('title')}</p>
        <p className={classes.changesBody}>
          {k('body', { revision: revisionText(revision.revisionNo) })}
        </p>
      </div>
      <Button size="xs" leftSection={<IconSignature size={15} />} onClick={onConfirm}>
        {k('confirm')}
      </Button>
    </section>
  );
}

/** Okuma beyanı penceresi: kişi kendi adına beyan eder; parola isteğe bağlı (ADR 0056). */
export function ReadDialog({
  opened,
  detail,
  revision,
  onClose,
  onConfirm,
}: {
  opened: boolean;
  detail: LibraryDetail;
  revision: LibraryRevision;
  onClose: () => void;
  onConfirm: (password: string) => Promise<void>;
}) {
  const { t } = useTranslation();
  const k = (key: string, options?: Record<string, unknown>) =>
    t(`documents.read.${key}`, options ?? {});
  // Teyit ve "okudum"da parola isteğe bağlı (ADR 0056 §1): alan yerine bilgi metni; şirket ayarı
  // açıksa ya da sunucu isterse alan görünür. Boş parola "parolasız" demektir
  const reauth = useReauth('document_read');
  const { reset: resetReauth } = reauth;
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Pencere bağlı kalır (odak dönüşü; S5): kapanınca parola ve hata silinir
  useEffect(() => {
    if (opened) return;
    resetReauth();
    setError(null);
  }, [opened, resetReauth]);
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (reauth.missing) return;
    setBusy(true);
    setError(null);
    try {
      await onConfirm(reauth.body().password ?? '');
      reauth.reset();
    } catch (failure) {
      reauth.noteError(failure);
      const code = (failure as { code?: string }).code ?? 'GENERIC';
      setError(t(`documents.errors.${code}`, { defaultValue: t('documents.errors.GENERIC') }));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={k('dialogTitle')}
      closeButtonProps={{ 'aria-label': t('documents.screen.dismiss') }}
      centered
    >
      <form onSubmit={(event) => void submit(event)}>
        <Stack gap="md">
          <Text size="sm">
            {k('dialogBody', {
              document: documentHeading(detail),
              revision: revisionText(revision.revisionNo),
            })}
          </Text>
          {error ? (
            <Alert color="red" icon={<IconAlertTriangle size={16} />} role="alert">
              {error}
            </Alert>
          ) : null}
          <ReauthField
            reauth={reauth}
            label={k('password')}
            description={k('passwordHint')}
            mb="0"
          />
          <Group justify="flex-end">
            <Button variant="default" onClick={onClose}>
              {k('cancel')}
            </Button>
            <Button type="submit" loading={busy} disabled={reauth.missing}>
              {k('confirm')}
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}
